import { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Badge,
  Group,
  Tooltip,
  Table,
  Paper,
  ScrollArea,
  Loader,
  Alert,
  Modal,
  Button,
  TextInput,
  Pagination,
  Center,
  Divider,
  Tabs,
  UnstyledButton,
  Select,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconRefresh,
  IconSearch,
  IconFilter,
  IconClock,
  IconCircleCheck,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  useErrorReports,
  useResolveErrorReport,
} from "../../hooks/useWorkflows";
import { useLinkableCorrectionsForCase } from "../../hooks/useCorrectionRequests";
import { sanitizeHtml } from "../../utils/sanitize";
import {
  ErrorReportFilters,
  ErrorReport,
} from "../../services/workflows.service";
import {
  ErrorReportFilterModal,
  ErrorReportActiveFilters,
} from "../../components/errors";
import { getDisplayCaseNumber } from "../../utils/caseIdentity";
import {
  getErrorDisplayName,
  errorRefLabel,
  correctionRefLabel,
} from "../../utils/formatters";
import RichTextDiagnosis from "../../components/pathologist-review/RichTextDiagnosis";
import { stripHtml } from "../../utils/helpers";

const ITEMS_PER_PAGE = 20;

export default function ErrorResolutionPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL state
  const tab = searchParams.get("tab") || "active";
  const pageStr = searchParams.get("page");
  const page = pageStr ? parseInt(pageStr, 10) : 1;
  const sort = searchParams.get("sort") || "desc";
  const filterParam = searchParams.get("filter") || "";
  const search = searchParams.get("search") || "";

  const [debouncedSearch] = useDebouncedValue(search, 300);

  const filters: ErrorReportFilters = useMemo(() => {
    if (!filterParam) return {};
    try {
      return JSON.parse(decodeURIComponent(filterParam));
    } catch {
      return {};
    }
  }, [filterParam]);

  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);

  // Modal state
  const [selectedReport, setSelectedReport] = useState<ErrorReport | null>(
    null
  );
  const [
    resolveModalOpened,
    { open: openResolveModal, close: closeResolveModal },
  ] = useDisclosure(false);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [linkedCorrectionId, setLinkedCorrectionId] = useState<string | null>(
    null
  );

  const updateUrlParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null) {
            newParams.delete(key);
          } else {
            newParams.set(key, value);
          }
        });
        return newParams;
      });
    },
    [setSearchParams]
  );

  const handleTabChange = useCallback(
    (newTab: string | null) => {
      if (!newTab) return;
      updateUrlParams({ tab: newTab, page: "1" });
    },
    [updateUrlParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateUrlParams({ page: newPage.toString() });
    },
    [updateUrlParams]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      updateUrlParams({ search: value || null, page: "1" });
    },
    [updateUrlParams]
  );

  const handleSortChange = useCallback(() => {
    updateUrlParams({ sort: sort === "desc" ? "asc" : "desc", page: "1" });
  }, [sort, updateUrlParams]);

  const handleFiltersApply = useCallback(
    (newFilters: ErrorReportFilters) => {
      const { status, ...filtersWithoutStatus } = newFilters;
      const filterString =
        Object.keys(filtersWithoutStatus).length > 0
          ? encodeURIComponent(JSON.stringify(filtersWithoutStatus))
          : null;
      updateUrlParams({ filter: filterString, page: "1" });
    },
    [updateUrlParams]
  );

  const handleClearAllFilters = useCallback(() => {
    updateUrlParams({ filter: null, search: null, page: "1" });
  }, [updateUrlParams]);

  // Build filters for API - pending errors
  const pendingFilters: ErrorReportFilters = useMemo(() => {
    return {
      ...filters,
      status: "pending",
      search: debouncedSearch || undefined,
    };
  }, [filters, debouncedSearch]);

  // Build filters for API - resolved errors
  const resolvedFilters: ErrorReportFilters = useMemo(() => {
    return {
      ...filters,
      status: "resolved",
      search: debouncedSearch || undefined,
    };
  }, [filters, debouncedSearch]);

  // Fetch data for both tables
  const {
    data: pendingReports = [],
    isLoading: isPendingLoading,
    error: pendingError,
    refetch: refetchPending,
  } = useErrorReports(pendingFilters);

  const {
    data: resolvedReports = [],
    isLoading: isResolvedLoading,
    error: resolvedError,
    refetch: refetchResolved,
  } = useErrorReports(resolvedFilters);

  const resolveMutation = useResolveErrorReport();

  const { data: correctionsData } = useLinkableCorrectionsForCase(
    selectedReport?.caseId,
    resolveModalOpened && selectedReport?.status === "pending"
  );

  const correctionOptions = useMemo(
    () =>
      (correctionsData?.correctionRequests ?? []).map((cr) => ({
        value: cr.id,
        label: `${
          correctionRefLabel(cr.refNumber)
            ? `${correctionRefLabel(cr.refNumber)} — `
            : ""
        }${cr.requestor.name ?? "Unknown"} · ${
          Object.keys(cr.proposedChanges ?? {}).length
        } field change(s) · ${new Date(cr.requestedAt).toLocaleDateString()}`,
      })),
    [correctionsData]
  );

  // Sorting
  const sortedPendingReports = useMemo(() => {
    return [...pendingReports].sort((a, b) => {
      const dateA = new Date(a.reportedAt).getTime();
      const dateB = new Date(b.reportedAt).getTime();
      return sort === "desc" ? dateB - dateA : dateA - dateB;
    });
  }, [pendingReports, sort]);

  const sortedResolvedReports = useMemo(() => {
    return [...resolvedReports].sort((a, b) => {
      const dateA = new Date(a.reportedAt).getTime();
      const dateB = new Date(b.reportedAt).getTime();
      return sort === "desc" ? dateB - dateA : dateA - dateB;
    });
  }, [resolvedReports, sort]);

  // Pagination for active tab
  const activeReports = useMemo(
    () => (tab === "active" ? sortedPendingReports : sortedResolvedReports),
    [tab, sortedPendingReports, sortedResolvedReports]
  );
  const activeTotalPages = useMemo(
    () => Math.ceil(activeReports.length / ITEMS_PER_PAGE),
    [activeReports]
  );
  const paginatedReports = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return activeReports.slice(start, start + ITEMS_PER_PAGE);
  }, [activeReports, page]);

  // Check if any filters are active (excluding status)
  const hasActiveFilters = useMemo(() => {
    return (
      (filters.categories && filters.categories.length > 0) ||
      filters.blocking !== undefined ||
      filters.fromDate !== undefined ||
      filters.toDate !== undefined
    );
  }, [filters]);

  // Handlers
  const handleViewCase = useCallback(
    (caseId: string) => {
      navigate(`/cases/${caseId}`);
    },
    [navigate]
  );

  const handleResolve = useCallback(
    (report: ErrorReport) => {
      setSelectedReport(report);
      setResolutionNotes("");
      setLinkedCorrectionId(null);
      openResolveModal();
    },
    [openResolveModal]
  );

  const handleViewDetails = useCallback(
    (report: ErrorReport) => {
      setSelectedReport(report);
      openResolveModal();
    },
    [openResolveModal]
  );

  const handleConfirmResolve = useCallback(async () => {
    if (!selectedReport) return;

    try {
      await resolveMutation.mutateAsync({
        errorId: selectedReport.id,
        data: { resolutionNotes, correctionRequestId: linkedCorrectionId },
      });

      notifications.show({
        title: "Success",
        message: "Error report resolved",
        color: "green",
      });

      closeResolveModal();
      setSelectedReport(null);
      setLinkedCorrectionId(null);
      refetchPending();
      refetchResolved();
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: "Failed to resolve error report",
        color: "red",
      });
    }
  }, [
    selectedReport,
    resolutionNotes,
    linkedCorrectionId,
    resolveMutation,
    closeResolveModal,
    refetchPending,
    refetchResolved,
  ]);

  const getCategoryColor = useCallback((category: string) => {
    switch (category?.toLowerCase()) {
      case "accessioning":
        return "blue";
      case "grossing":
        return "grape";
      case "processing":
        return "cyan";
      default:
        return "gray";
    }
  }, []);

  const formatDateTime = useCallback((dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.toLocaleDateString()}, ${date.toLocaleTimeString()}`;
  }, []);

  const refetchAll = useCallback(() => {
    refetchPending();
    refetchResolved();
  }, [refetchPending, refetchResolved]);

  const isPending = tab === "active";
  const isLoading = isPending ? isPendingLoading : isResolvedLoading;

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <Center p="xl">
          <Loader />
        </Center>
      );
    }

    if (activeReports.length === 0) {
      return (
        <div style={{ padding: "3rem", textAlign: "center" }}>
          <IconAlertCircle
            size={48}
            color="gray"
            style={{ margin: "0 auto" }}
          />
          <Text size="lg" c="dimmed" mt="md">
            {isPending ? "No pending errors" : "No resolved errors"}
          </Text>
          <Text size="sm" c="dimmed" mt="xs">
            {hasActiveFilters || debouncedSearch
              ? "No reports match your filter criteria"
              : isPending
              ? "All errors have been resolved"
              : "No errors have been resolved yet"}
          </Text>
        </div>
      );
    }

    return (
      <Stack gap="md">
        {/* Pagination Header */}
        <Group justify="space-between" align="center" mb="sm">
          <Group gap="md">
            <Text size="sm" c="dimmed">
              {activeReports.length} {isPending ? "pending" : "resolved"} error
              {activeReports.length !== 1 ? "s" : ""}
            </Text>
            {activeTotalPages > 1 && (
              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                  {Math.min(page * ITEMS_PER_PAGE, activeReports.length)} of{" "}
                  {activeReports.length}
                </Text>
                <Pagination
                  total={activeTotalPages}
                  value={page}
                  onChange={handlePageChange}
                  size="sm"
                />
              </Group>
            )}
          </Group>
          {isPending && activeReports.some((report) => report.blockCase) && (
            <Group gap="xs">
              <IconAlertTriangle size={16} color="var(--mantine-color-red-6)" />
              <Text size="sm" c="red" fw={500}>
                Errors highlighted in red block case progression.
              </Text>
            </Group>
          )}
        </Group>

        {/* Table */}
        <ScrollArea>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Case Number</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Error</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>
                  <UnstyledButton
                    onClick={handleSortChange}
                    p={0}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Text fw={700} size="sm">
                      Reported
                    </Text>
                    {sort === "desc" ? (
                      <IconChevronDown size={16} />
                    ) : (
                      <IconChevronUp size={16} />
                    )}
                  </UnstyledButton>
                </Table.Th>
                {!isPending && <Table.Th>Resolved</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginatedReports.map((report) => (
                <Table.Tr
                  key={report.id}
                  bg={
                    isPending && report.blockCase
                      ? "var(--mantine-color-red-light)"
                      : undefined
                  }
                >
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      {errorRefLabel(report.refNumber) && (
                        <Badge size="xs" variant="outline" color="blue">
                          {errorRefLabel(report.refNumber)}
                        </Badge>
                      )}
                      <Text
                        size="sm"
                        fw={500}
                        style={{
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        c="blue"
                        onClick={() => handleViewCase(report.caseId)}
                      >
                        {getDisplayCaseNumber(report.case) || "N/A"}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="sm"
                      variant="light"
                      color={getCategoryColor(
                        report.discrepancy?.category ||
                          report.error?.category ||
                          ""
                      )}
                    >
                      {report.discrepancy?.category ||
                        report.error?.category ||
                        "Unknown"}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {getErrorDisplayName(
                        report.discrepancy?.error || report.error?.error
                      )}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Tooltip
                      label={stripHtml(report.description)}
                      multiline
                      w={300}
                      disabled={
                        !report.description ||
                        stripHtml(report.description).length < 50
                      }
                    >
                      <Box
                        style={{
                          maxWidth: "200px",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(report.description || "-"),
                        }}
                      />
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <Group justify="space-between" wrap="nowrap">
                      <Box>
                        <Text size="sm" fw={500}>
                          {report.reportedBy?.fullName ||
                            report.reportedByUser?.fullName ||
                            "Unknown"}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatDateTime(report.reportedAt)}
                        </Text>
                      </Box>
                      {isPending && (
                        <Button
                          variant="filled"
                          color="green"
                          size="sm"
                          onClick={() => handleResolve(report)}
                        >
                          Resolve
                        </Button>
                      )}
                    </Group>
                  </Table.Td>
                  {!isPending && (
                    <Table.Td>
                      <Group justify="space-between" wrap="nowrap">
                        <Box>
                          <Text size="sm" fw={500}>
                            {report.resolvedBy?.fullName || "Unknown"}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {formatDateTime(report.resolvedAt || "")}
                          </Text>
                        </Box>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleViewDetails(report)}
                        >
                          Details
                        </Button>
                      </Group>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Stack>
    );
  };

  if (pendingError || resolvedError) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load error reports. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Container fluid py="xl">
        <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
          <Stack gap="lg">
            {/* Header */}
            <Title order={2}>Error Resolution</Title>

            {/* Search and Filters Row */}
            <Group align="stretch" grow>
              {/* Search Section */}
              <Paper p="md" withBorder style={{ flex: 2 }}>
                <Stack gap="sm">
                  <Group>
                    <IconSearch size={24} />
                    <Text fw={500}>Search</Text>
                  </Group>
                  <TextInput
                    placeholder="Search by case number, error name, or description..."
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    leftSection={<IconSearch size={16} />}
                  />
                  <Text size="sm" c="dimmed">
                    Search across case numbers, error types, and descriptions
                  </Text>
                </Stack>
              </Paper>

              {/* Filters Section */}
              <Paper p="md" withBorder style={{ flex: 1 }}>
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Group>
                      <IconFilter size={24} />
                      <Text fw={500}>Filters</Text>
                    </Group>
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      onClick={refetchAll}
                      loading={isPendingLoading || isResolvedLoading}
                      leftSection={<IconRefresh size={16} />}
                    >
                      Refresh
                    </Button>
                  </Group>
                  <Button variant="light" onClick={openFilterModal} fullWidth>
                    {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                  </Button>
                  {hasActiveFilters && (
                    <ErrorReportActiveFilters
                      filters={filters}
                      onFiltersChange={handleFiltersApply}
                      onClearAll={handleClearAllFilters}
                    />
                  )}
                  <Text size="sm" c="dimmed">
                    {pendingReports.length} pending, {resolvedReports.length}{" "}
                    resolved
                  </Text>
                </Stack>
              </Paper>
            </Group>

            <Tabs value={tab} onChange={handleTabChange}>
              <Tabs.List>
                <Tabs.Tab
                  value="active"
                  leftSection={<IconClock size={16} />}
                  rightSection={
                    <Badge
                      w={16}
                      h={16}
                      p={0}
                      variant="filled"
                      color="yellow"
                      size="sm"
                    >
                      {pendingReports.length}
                    </Badge>
                  }
                >
                  Active Errors
                </Tabs.Tab>
                <Tabs.Tab
                  value="resolved"
                  leftSection={<IconCircleCheck size={16} />}
                  rightSection={
                    <Badge
                      w={16}
                      h={16}
                      p={0}
                      variant="filled"
                      color="green"
                      size="sm"
                    >
                      {resolvedReports.length}
                    </Badge>
                  }
                >
                  Resolved Errors
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value={tab} pt="md">
                <Paper p="md" withBorder>
                  {renderTableContent()}
                </Paper>
              </Tabs.Panel>
            </Tabs>
          </Stack>
        </Box>
      </Container>

      {/* Filter Modal */}
      <ErrorReportFilterModal
        opened={filterModalOpened}
        onClose={closeFilterModal}
        filters={filters}
        onApply={handleFiltersApply}
      />

      {/* Error Details / Resolve Modal */}
      <Modal.Root
        opened={resolveModalOpened}
        onClose={closeResolveModal}
        size="xl"
        centered
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>
              <Group gap="xs">
                {selectedReport?.status === "resolved" ? (
                  <IconCheck size={20} color="var(--mantine-color-green-6)" />
                ) : (
                  <IconAlertTriangle
                    size={20}
                    color="var(--mantine-color-red-6)"
                  />
                )}
                <Text fw={600}>Error Details</Text>
                {errorRefLabel(selectedReport?.refNumber) && (
                  <Badge size="sm" variant="outline" color="blue">
                    {errorRefLabel(selectedReport?.refNumber)}
                  </Badge>
                )}
              </Group>
            </Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>

          <Modal.Body style={{ flex: 1, overflow: "auto" }}>
            {selectedReport && (
              <Stack gap="md" mt="sm">
                {/* Error Type */}
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Error Type
                  </Text>
                  <Text size="sm" fw={500}>
                    {getErrorDisplayName(
                      selectedReport.discrepancy?.error ||
                        selectedReport.error?.error
                    )}
                  </Text>
                  {(selectedReport.discrepancy?.description ||
                    selectedReport.error?.description) && (
                    <Text size="xs" c="dimmed">
                      {selectedReport.discrepancy?.description ||
                        selectedReport.error?.description}
                    </Text>
                  )}
                </div>

                {/* Description */}
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Description
                  </Text>
                  <Box
                    style={{
                      fontSize: "var(--mantine-font-size-sm)",
                      marginTop: 4,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(selectedReport.description || "-"),
                    }}
                  />
                </div>

                {/* Flags */}
                <Group gap="xs">
                  {selectedReport.blockCase && (
                    <Badge color="red" variant="filled">
                      Blocks Case
                    </Badge>
                  )}
                  {selectedReport.preventSignOff && (
                    <Badge color="orange" variant="filled">
                      Prevents Sign-Off
                    </Badge>
                  )}
                </Group>

                <Divider />

                {/* Reported By */}
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Reported By
                  </Text>
                  <Text size="sm">
                    {selectedReport.reportedBy?.fullName ||
                      selectedReport.reportedByUser?.fullName ||
                      "Unknown"}{" "}
                    on {formatDateTime(selectedReport.reportedAt)}
                  </Text>
                </div>

                {/* Resolution Info (if resolved) */}
                {selectedReport.status === "resolved" && (
                  <>
                    <Divider />
                    <div>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Resolved By
                      </Text>
                      <Text size="sm">
                        {selectedReport.resolvedBy?.fullName || "Unknown"} on{" "}
                        {formatDateTime(selectedReport.resolvedAt || "")}
                      </Text>
                    </div>
                    {selectedReport.resolutionNotes && (
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Resolution Notes
                        </Text>
                        <Box
                          style={{
                            fontSize: "var(--mantine-font-size-sm)",
                            marginTop: 4,
                          }}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(
                              selectedReport.resolutionNotes
                            ),
                          }}
                        />
                      </div>
                    )}
                    {selectedReport.correctionRequestId && (
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Linked Correction
                        </Text>
                        <Button
                          variant="subtle"
                          size="xs"
                          mt={4}
                          onClick={() =>
                            navigate(
                              `/cases/${selectedReport.caseId}?tab=corrections`
                            )
                          }
                        >
                          {selectedReport.correctionRequest?.refNumber != null
                            ? `View Linked Correction C${selectedReport.correctionRequest.refNumber}`
                            : "View Linked Correction"}
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {/* Resolution Form (if pending) */}
                {selectedReport.status === "pending" && (
                  <>
                    <Divider />
                    <div>
                      <Text
                        size="xs"
                        c="dimmed"
                        tt="uppercase"
                        fw={600}
                        mb="xs"
                      >
                        Resolution Notes
                      </Text>
                      <RichTextDiagnosis
                        value={resolutionNotes}
                        onChange={setResolutionNotes}
                        placeholder="Enter resolution notes..."
                      />
                    </div>
                    <div>
                      <Text
                        size="xs"
                        c="dimmed"
                        tt="uppercase"
                        fw={600}
                        mb="xs"
                      >
                        Link Correction (Optional)
                      </Text>
                      <Select
                        placeholder={
                          correctionOptions.length === 0
                            ? "No corrections found for this case"
                            : "Search corrections for this case..."
                        }
                        searchable
                        clearable
                        data={correctionOptions}
                        value={linkedCorrectionId}
                        onChange={setLinkedCorrectionId}
                        disabled={correctionOptions.length === 0}
                        nothingFoundMessage="No results"
                      />
                    </div>
                  </>
                )}
              </Stack>
            )}
          </Modal.Body>

          <Divider />
          <Group justify="space-between" p="md" gap="sm">
            <Button variant="default" onClick={closeResolveModal}>
              {selectedReport?.status === "resolved" ? "Close" : "Cancel"}
            </Button>

            {selectedReport?.status === "pending" && (
              <Button
                color="green"
                onClick={handleConfirmResolve}
                loading={resolveMutation.isPending}
              >
                Mark as Resolved
              </Button>
            )}
          </Group>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}
